import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * Autocomplete input component with suggestions dropdown
 * @param {string} value - Current input value
 * @param {Function} onChange - Callback when value changes
 * @param {string[]} suggestions - Array of suggestion strings
 * @param {boolean} loading - Whether suggestions are loading
 * @param {string} placeholder - Input placeholder text
 * @param {string} type - Input type (default: 'text')
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether input is disabled
 * @param {Function} onFocus - Focus event handler
 * @param {Function} onBlur - Blur event handler
 */
const AutocompleteInput = ({
  value = '',
  onChange,
  suggestions = [],
  loading = false,
  placeholder = '',
  type = 'text',
  className = '',
  disabled = false,
  onFocus,
  onBlur,
  ...inputProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Filter suggestions based on input value (case-insensitive)
  const filteredSuggestions = useMemo(() => {
    if (!value || !suggestions.length) {
      return suggestions;
    }

    const lowerValue = value.toLowerCase();
    return suggestions.filter(suggestion => {
      const suggestionStr = String(suggestion).toLowerCase();
      return suggestionStr.includes(lowerValue) && suggestionStr !== lowerValue;
    });
  }, [value, suggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && filteredSuggestions.length > 0 && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          handleSelectSuggestion(filteredSuggestions[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;

      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = (e) => {
    setIsOpen(filteredSuggestions.length > 0);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleInputBlur = (e) => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      if (
        !dropdownRef.current ||
        !dropdownRef.current.contains(document.activeElement)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (onBlur) {
          onBlur(e);
        }
      }
    }, 200);
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(String(suggestion));
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Highlight matching text in suggestion
  const highlightText = (text, query) => {
    if (!query || !text) return text;

    const lowerText = String(text).toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const afterMatch = text.substring(index + query.length);

    return (
      <>
        {beforeMatch}
        <strong className="font-semibold text-teal-600 dark:text-teal-400">{match}</strong>
        {afterMatch}
      </>
    );
  };

  const inputClasses = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
    className || ''
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
          {...inputProps}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            tabIndex={-1}
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
          ) : (
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {loading && filteredSuggestions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              Loading suggestions...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            <ul className="py-1">
              {filteredSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`px-3 py-2 cursor-pointer text-sm ${
                    index === highlightedIndex
                      ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                      : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {highlightText(String(suggestion), value)}
                </li>
              ))}
            </ul>
          ) : value ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              No suggestions found
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              Start typing to see suggestions
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;

